import { useState, useEffect } from 'react';
import { PrevArrow, NextArrow, UpArrow, DownArrow, icn_calendar, icn_clock } from '../../imports';
import { useBooking } from '../../context/contexts';
import { useCalendar } from '../../context/CalendarContext';
import { formatDisplayDate, formatTimeDisplay } from '../../lib/utils/dateTimeFormat';
import './Calendar.css';

const Calendar = () => {
    const { selectDateTime } = useBooking();
    const { 
        loading,
        error,
        getDaysInMonth,
        getAvailableTimeSlots,
        getFormattedMonthYear,
        goToPrevMonth,
        goToNextMonth,
        selectedDate,
        setSelectedDate,
        currentMonth,
        recurringScheduleData
    } = useCalendar();
    
    const [selectedTime, setSelectedTime] = useState("");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [calendarError, setCalendarError] = useState(null);

    // Update available times when selected date changes
    useEffect(() => {
        if (selectedDate) {
            try {
                const timeSlots = getAvailableTimeSlots(selectedDate);
                // Only show time slots that are available (not booked and enabled in recurring schedule)
                const available = timeSlots.filter(slot => slot.available);
                setAvailableTimes(available);
                
                // Clear selected time if it's no longer available
                if (selectedTime && !available.find(slot => slot.time === selectedTime)) {
                    setSelectedTime("");
                }
            } catch (err) {
                setCalendarError('Failed to load available times');
                setAvailableTimes([]);
            }
        } else {
            setAvailableTimes([]);
            setSelectedTime("");
        }
    }, [selectedDate, getAvailableTimeSlots, selectedTime]);

    const handleDateSelect = (day) => {
        if (!day.disabled && day.available !== false) {
            // Use the exact date from the day object without any manipulation
            setSelectedDate(day.date);
            setShowDatePicker(false);
            setCalendarError(null);
        }
    };

    const handleTimeSelect = (time) => {
        // Only allow selecting available time slots
        const slot = availableTimes.find(slot => slot.time === time);
        if (slot) {
            setSelectedTime(time);
            setShowTimePicker(false);
            setCalendarError(null);
            
            // Update booking context
            if (selectedDate && time) {
                selectDateTime(selectedDate, time);
            }
        }
    };

    const days = getDaysInMonth();
    const monthYear = getFormattedMonthYear(currentMonth);
    // Use full weekday names as keys to avoid duplicates
    const weekDays = [
        { key: 'sunday', label: 'S' },
        { key: 'monday', label: 'M' },
        { key: 'tuesday', label: 'T' },
        { key: 'wednesday', label: 'W' },
        { key: 'thursday', label: 'T' },
        { key: 'friday', label: 'F' },
        { key: 'saturday', label: 'S' }
    ];

    return (
        <div className="datetime-picker">
            {loading && <div className="datetime-picker__loading">Loading calendar...</div>}
            {error && <div className="datetime-picker__error">Error: {error}</div>}
            {calendarError && <div className="datetime-picker__error">Error: {calendarError}</div>}
            
            <div className="datetime-picker__container">
                <div className="datetime-picker__date">
                    <div 
                        className="datetime-picker__input"
                        onClick={() => setShowDatePicker(!showDatePicker)}
                    >
                        <img src={icn_calendar} alt="Calendar" className="datetime-picker__icon" />
                        <span>{selectedDate ? formatDisplayDate(selectedDate) : 'Select a date'}</span>
                        {showDatePicker ? <UpArrow /> : <DownArrow />}
                    </div>
                    
                    {showDatePicker && (
                        <div className="datetime-picker__calendar">
                            <div className="calendar-header">
                                <button onClick={goToPrevMonth}><PrevArrow /></button>
                                <span>{monthYear}</span>
                                <button onClick={goToNextMonth}><NextArrow /></button>
                            </div>
                            <div className="calendar-weekdays">
                                {weekDays.map(day => (
                                    <div key={day.key} className="weekday">{day.label}</div>
                                ))}
                            </div>
                            <div className="calendar-days">
                                {days.map((day, index) => (
                                    <div
                                        key={`${day.day}-${index}`}
                                        className={`calendar-day 
                                                  ${day.disabled ? 'disabled' : ''} 
                                                  ${selectedDate === day.date ? 'selected' : (day.isToday ? 'today' : '')} 
                                                  ${day.day ? '' : 'empty'}
                                                  ${day.available === false ? 'unavailable' : ''}`}
                                        onClick={() => handleDateSelect(day)}
                                    >
                                        {day.day}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="datetime-picker__time">
                    <div 
                        className={`datetime-picker__input ${!selectedDate ? 'disabled' : ''}`}
                        onClick={() => selectedDate && setShowTimePicker(!showTimePicker)}
                    >
                        <img src={icn_clock} alt="Clock" className="datetime-picker__icon" />
                        <span>{formatTimeDisplay(selectedTime)}</span>
                        {showTimePicker ? <UpArrow /> : <DownArrow />}
                    </div>
                    
                    {showTimePicker && selectedDate && (
                        <div className="datetime-picker__times">
                            {availableTimes.length > 0 ? (
                                availableTimes.map(slot => (
                                    <div
                                        key={slot.time}
                                        className={`time-slot ${selectedTime === slot.time ? 'selected' : ''}`}
                                        onClick={() => handleTimeSelect(slot.time)}
                                    >
                                        {slot.time}
                                    </div>
                                ))
                            ) : (
                                <div className="time-slot-empty">No available times for this date</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Calendar;